import { useEffect, useRef, useState, useCallback } from 'react';
import { io, type Socket } from 'socket.io-client';
import type { AgentEvent } from '@/types';

interface UseWebSocketReturn {
  connected: boolean;
  lastEvent: AgentEvent | null;
  subscribe: <T extends AgentEvent['type']>(
    eventType: T,
    handler: (data: Extract<AgentEvent, { type: T }>) => void,
  ) => () => void;
}

export function useWebSocket(sessionId?: string | null): UseWebSocketReturn {
  const socketRef = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState<AgentEvent | null>(null);
  const handlersRef = useRef<Map<string, Set<(...args: unknown[]) => void>>>(new Map());

  useEffect(() => {
    if (!sessionId) return;

    // 后端 gateway 命名空间为 '/agent'，必须连到该命名空间否则收不到 playable:ready / asset:ready 等事件
    const socket = io(`${window.location.origin}/agent`, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    socket.on('connect', () => { setConnected(true); socket.emit('join', { sessionId }); });
    socket.on('disconnect', () => setConnected(false));

    // Listen for backend events
    const eventNames = ['step:update', 'asset:ready', 'playable:ready', 'audio:ready', 'qa:report', 'error', 'session:title-updated', 'pipeline:stage'];
    for (const name of eventNames) {
      socket.on(name, (data: unknown) => {
        setLastEvent({ type: name as AgentEvent['type'], ...(data as Record<string, unknown>) } as AgentEvent);
        const handlers = handlersRef.current.get(name);
        if (handlers) for (const h of handlers) { try { h(data); } catch {} }
      });
    }

    return () => { socket.disconnect(); socketRef.current = null; setConnected(false); };
  }, [sessionId]);

  const subscribe = useCallback(
    <T extends AgentEvent['type']>(
      eventType: T,
      handler: (data: Extract<AgentEvent, { type: T }>) => void,
    ) => {
      if (!handlersRef.current.has(eventType)) {
        handlersRef.current.set(eventType, new Set());
      }
      const handlers = handlersRef.current.get(eventType)!;
      handlers.add(handler as (...args: unknown[]) => void);

      return () => {
        handlers.delete(handler as (...args: unknown[]) => void);
      };
    },
    [],
  );

  return { connected, lastEvent, subscribe };
}
